import { useCallback, useEffect, useId, useMemo, type ReactNode } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { useIntl, type IntlShape } from 'react-intl';
import { z } from 'zod';
import { type IssuedTicketRevokeDTO } from '@/api/trafikportalen';
import { revokeIssuedTicketBodyRevokeReasonDetailMax } from '@/api/trafikportalen/trafikportalenAPI.zod';
import Input from '@/components/core/Input';
import { WarningDialog } from '@/components/WarningDialog';

const createCancelTicketSchema = (formatMessage: IntlShape['formatMessage']) =>
  z.object({
    revoke_reason_detail: z
      .string()
      .trim()
      .max(
        revokeIssuedTicketBodyRevokeReasonDetailMax,
        formatMessage(
          { defaultMessage: 'Anledningen får vara högst {max} tecken' },
          { max: revokeIssuedTicketBodyRevokeReasonDetailMax },
        ),
      ),
  });

type CancelTicketFormValues = z.infer<
  ReturnType<typeof createCancelTicketSchema>
>;

interface CancelIssuedTicketDialogProps {
  open: boolean;
  description: ReactNode;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: (data: IssuedTicketRevokeDTO) => void;
}

export const CancelIssuedTicketDialog = ({
  open,
  description,
  loading = false,
  onCancel,
  onConfirm,
}: CancelIssuedTicketDialogProps) => {
  const { formatMessage } = useIntl();
  const detailId = useId();
  const schema = useMemo(
    () => createCancelTicketSchema(formatMessage),
    [formatMessage],
  );
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CancelTicketFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      revoke_reason_detail: '',
    },
  });

  useEffect(() => {
    if (!open) {
      reset({ revoke_reason_detail: '' });
    }
  }, [open, reset]);

  const submit = useCallback(
    (values: CancelTicketFormValues) => {
      onConfirm(
        values.revoke_reason_detail
          ? { revoke_reason_detail: values.revoke_reason_detail }
          : {},
      );
    },
    [onConfirm],
  );

  const handleConfirm = useCallback(() => {
    void handleSubmit(submit)();
  }, [handleSubmit, submit]);

  return (
    <WarningDialog
      title={formatMessage({
        defaultMessage: 'Avsluta biljett',
      })}
      content={
        <div className="flex flex-col gap-4">
          <p>{description}</p>
          <DetailField
            id={detailId}
            disabled={loading}
            error={errors.revoke_reason_detail?.message}
            registration={register('revoke_reason_detail')}
            label={formatMessage({ defaultMessage: 'Anledning' })}
            helperText={formatMessage(
              { defaultMessage: 'Högst {max} tecken' },
              { max: revokeIssuedTicketBodyRevokeReasonDetailMax },
            )}
          />
        </div>
      }
      onCancel={onCancel}
      onConfirm={handleConfirm}
      buttonText={formatMessage({ defaultMessage: 'Avsluta' })}
      loading={loading}
      open={open}
    />
  );
};

interface DetailFieldProps {
  id: string;
  label: string;
  helperText: string;
  error?: string;
  disabled: boolean;
  registration: UseFormRegisterReturn<'revoke_reason_detail'>;
}

const DetailField = ({
  id,
  label,
  helperText,
  error,
  disabled,
  registration,
}: DetailFieldProps) => (
  <div className="pt-6">
    <Input
      id={id}
      type="text"
      label={label}
      maxLength={revokeIssuedTicketBodyRevokeReasonDetailMax}
      disabled={disabled}
      autoComplete="off"
      error={error}
      helperText={helperText}
      helperTextReserveSpace
      name={registration.name}
      ref={registration.ref}
      onBlur={(event) => {
        void registration.onBlur(event);
      }}
      onChange={(event) => {
        void registration.onChange(event);
      }}
    />
  </div>
);
